import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { placePopover } from './datePickerModel';
import {
  firstEnabled, isNativeOnly, lastEnabled, stepIndex, typeahead, type SelectItem,
} from './selectLayerModel';

/**
 * P-57b (D-232 sec.2 / QX-601) - ONE designed list for every native `<select>`.
 * Mounted once in the shell (like DatePickerLayer); it listens on the document,
 * so no form has to change and none can be missed:
 *  - a click on a select opens the designed list instead of the browser's popup;
 *  - values, defaults, `disabled`, `required`, onChange all stay the select's own:
 *    the pick is written to the real element and a bubbling `input` + `change`
 *    event is dispatched, so React-controlled and uncontrolled selects both see it;
 *  - focus stays on the select: arrows / Home / End move, Enter or Space picks,
 *    Escape closes, typing jumps to a match; aria-expanded / aria-activedescendant
 *    are mirrored onto the select, the list is role=listbox;
 *  - the list is portaled to <body> at `z-toast`, so it opens ABOVE dialogs
 *    (also on a phone, FQ-051), and closes on outside press / scroll / resize.
 * Multi-selects, listboxes (size>1) and `data-native-select` stay native.
 */

function selectOf(t: EventTarget | null): HTMLSelectElement | null {
  const el = t instanceof Element ? t.closest<HTMLSelectElement>('select') : null;
  if (!el || el.disabled || isNativeOnly(el)) return null;
  return el;
}

function readItems(sel: HTMLSelectElement): SelectItem[] {
  const out: SelectItem[] = [];
  Array.from(sel.options).forEach((o, index) => {
    if (o.hidden) return;
    const g = o.parentElement instanceof HTMLOptGroupElement ? o.parentElement : null;
    out.push({ index, label: o.label || o.text, disabled: o.disabled || Boolean(g?.disabled), group: g?.label });
  });
  return out;
}

function writeValue(sel: HTMLSelectElement, index: number) {
  if (sel.selectedIndex === index) return;
  sel.selectedIndex = index;
  sel.dispatchEvent(new Event('input', { bubbles: true }));
  sel.dispatchEvent(new Event('change', { bubbles: true }));
}

const PANEL_ID = 'select-layer-panel';

export default function SelectLayer() {
  const [sel, setSel] = useState<HTMLSelectElement | null>(null);
  const [items, setItems] = useState<SelectItem[]>([]);
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const buffer = useRef({ text: '', at: 0 });

  const close = useCallback(() => { setSel(null); setPos(null); }, []);

  const open = useCallback((el: HTMLSelectElement) => {
    const list = readItems(el);
    const cur = list.findIndex((x) => x.index === el.selectedIndex);
    setItems(list);
    setActive(cur >= 0 ? cur : firstEnabled(list));
    setPos(null);
    setSel(el);
  }, []);

  const pick = useCallback((i: number) => {
    const it = items[i];
    if (!sel || !it || it.disabled) return;
    writeValue(sel, it.index);
    sel.focus({ preventScroll: true });
    close();
  }, [sel, items, close]);

  // Open on a click anywhere on the select; stop the browser's own popup.
  useEffect(() => {
    const stopNative = (e: Event) => { if (selectOf(e.target)) e.preventDefault(); };
    const onClick = (e: MouseEvent) => {
      const el = selectOf(e.target);
      if (!el) return;
      e.preventDefault();
      if (el === sel) { close(); return; }
      el.focus({ preventScroll: true });
      open(el);
    };
    document.addEventListener('mousedown', stopNative, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('mousedown', stopNative, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [sel, open, close]);

  // Keyboard on a closed select: Space / F4 / Alt+Arrow open the designed list (plain arrows keep changing the value).
  useEffect(() => {
    if (sel) return;
    const onKey = (e: KeyboardEvent) => {
      const el = selectOf(e.target);
      if (!el || e.target !== el) return;
      if (e.key === ' ' || e.key === 'F4' || (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp'))) {
        e.preventDefault();
        open(el);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [sel, open]);

  // While open: outside press / scroll / resize close it; the keyboard drives it from the select.
  useEffect(() => {
    if (!sel) return;
    const onDown = (e: PointerEvent) => {
      const tg = e.target as Node;
      if (panel.current?.contains(tg) || sel.contains(tg)) return;
      close();
    };
    const onScroll = (e: Event) => { if (!panel.current?.contains(e.target as Node)) close(); };
    const onKey = (e: KeyboardEvent) => {
      if (e.target !== sel) return;
      const stop = () => { e.preventDefault(); e.stopPropagation(); };
      if (e.key === 'Escape') { stop(); close(); }
      else if (e.key === 'Tab') close();
      else if (e.key === 'ArrowDown') { stop(); setActive((a) => stepIndex(items, a, 1)); }
      else if (e.key === 'ArrowUp') { stop(); setActive((a) => stepIndex(items, a, -1)); }
      else if (e.key === 'Home') { stop(); setActive(firstEnabled(items)); }
      else if (e.key === 'End') { stop(); setActive(lastEnabled(items)); }
      else if (e.key === 'Enter' || e.key === ' ') { stop(); pick(active); }
      else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const now = Date.now();
        const b = buffer.current;
        b.text = now - b.at > 700 ? e.key : b.text + e.key;
        b.at = now;
        const hit = typeahead(items, b.text, active);
        if (hit >= 0) { stop(); setActive(hit); }
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey, true);
    const gone = window.setInterval(() => { if (!sel.isConnected || sel.disabled) close(); }, 400);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey, true);
      window.clearInterval(gone);
    };
  }, [sel, items, active, close, pick]);

  // Mirror the state onto the select so assistive tech announces it.
  useEffect(() => {
    if (!sel) return;
    sel.setAttribute('aria-haspopup', 'listbox');
    sel.setAttribute('aria-expanded', 'true');
    sel.setAttribute('aria-controls', PANEL_ID);
    return () => {
      sel.removeAttribute('aria-expanded');
      sel.removeAttribute('aria-controls');
      sel.removeAttribute('aria-activedescendant');
    };
  }, [sel]);
  useEffect(() => {
    if (sel && items[active]) sel.setAttribute('aria-activedescendant', `${PANEL_ID}-${active}`);
  }, [sel, items, active]);

  // Position once the panel has a size.
  useLayoutEffect(() => {
    if (!sel || !panel.current) return;
    const r = sel.getBoundingClientRect();
    const box = panel.current.getBoundingClientRect();
    setPos(placePopover(r, { width: box.width, height: box.height }, { width: window.innerWidth, height: window.innerHeight }));
  }, [sel, items]);

  // Keep the active item visible.
  useEffect(() => {
    if (!pos) return;
    panel.current?.querySelector<HTMLElement>(`#${PANEL_ID}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, pos]);

  const width = useMemo(() => (sel ? Math.max(sel.getBoundingClientRect().width, 160) : 160), [sel]);
  if (!sel) return null;

  const current = sel.selectedIndex;
  const rtl = document.dir !== 'ltr';
  return createPortal(
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus -- focus stays on the native select; keyboard managed centrally
    <div
      ref={panel}
      id={PANEL_ID}
      role="listbox"
      aria-label={sel.getAttribute('aria-label') ?? (sel.title || undefined)}
      dir={rtl ? 'rtl' : 'ltr'}
      onMouseDown={(e) => e.preventDefault() /* keep focus on the select */}
      style={{ position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? 'visible' : 'hidden', minWidth: width, maxWidth: 'min(28rem, calc(100vw - 16px))' }}
      className="z-toast max-h-64 overflow-y-auto rounded-xl border border-border bg-surface p-1 text-sm text-text shadow-lg"
    >
      {items.map((it, i) => {
        const selected = it.index === current;
        const showGroup = it.group && items[i - 1]?.group !== it.group;
        return (
          <div key={it.index}>
            {showGroup && <div className="px-3 pb-1 pt-2 text-xs font-semibold text-text-2">{it.group}</div>}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus -- focus stays on the native select; keyboard managed centrally */}
            <div
              id={`${PANEL_ID}-${i}`}
              role="option"
              aria-selected={selected}
              aria-disabled={it.disabled || undefined}
              onClick={() => pick(i)}
              onMouseEnter={() => { if (!it.disabled) setActive(i); }}
              className={`flex min-h-touch cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2 sm:min-h-0 sm:py-1.5 ${
                it.disabled ? 'cursor-not-allowed opacity-40'
                  : i === active ? 'bg-surface-subtle' : ''
              } ${selected ? 'font-semibold' : ''}`}
            >
              <span className="min-w-0 truncate">{it.label || ' '}</span>
              {selected && <span aria-hidden="true" className="text-accent">✓</span>}
            </div>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
